import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  MapPin, 
  Euro,
  Clock,
  Search, 
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Navigation,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import ZoneLivraisonEditor from './zones/ZoneLivraisonEditor';
import { MapContainer, TileLayer, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { normalizePolygonFromApi, toLeafletFormat, getPolygonCenter } from '../utils/polygonUtils';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ZonesLivraisonManager = ({ commercantId }) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [commercantLocation, setCommercantLocation] = useState(null); // {lat, lng}
  const [addModalKey, setAddModalKey] = useState(0); // Key pour forcer le remount à chaque ouverture

  useEffect(() => {
    if (commercantId) {
      fetchZones();
      fetchCommercantLocation();
    }
  }, [commercantId, showInactive]);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const data = await apiService.getZonesLivraison(commercantId, { includeInactive: showInactive });
      // L'API retourne directement un array, pas un objet avec .zones
      setZones(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Erreur lors du chargement des zones:', err);
      setError('Erreur lors du chargement des zones de livraison');
      setZones([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercantLocation = async () => {
    try {
      const commercant = await apiService.getCommercant(commercantId);
      // Utiliser les coordonnées override si présentes, sinon les coordonnées normales
      const lat = commercant.latitudeOverride ?? commercant.latitude;
      const lng = commercant.longitudeOverride ?? commercant.longitude;
      
      if (lat && lng) {
        setCommercantLocation({
          lat: parseFloat(lat),
          lng: parseFloat(lng)
        });
      } else {
        // Fallback sur Paris si pas de coordonnées
        setCommercantLocation({ lat: 48.8566, lng: 2.3522 });
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la localisation du commerçant:', err);
      // Fallback sur Paris en cas d'erreur
      setCommercantLocation({ lat: 48.8566, lng: 2.3522 });
    }
  };

  const handleAddZone = () => {
    setSelectedZone(null);
    setAddModalKey(prev => prev + 1); // Incrémenter la key pour forcer le remount
    setShowAddModal(true);
  };

  const handleEditZone = (zone) => {
    setSelectedZone(zone);
    setShowEditModal(true);
  };

  const handleSaveZone = async (zoneData) => {
    try {
      if (selectedZone) {
        await apiService.updateZoneLivraison(commercantId, selectedZone.id, zoneData);
      } else {
        await apiService.createZoneLivraison(commercantId, zoneData);
      }

      await fetchZones();
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedZone(null);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      alert('Erreur lors de la sauvegarde de la zone');
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette zone de livraison ?')) {
      try {
        await apiService.deleteZoneLivraison(commercantId, zoneId);
        await fetchZones();
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        alert('Erreur lors de la suppression de la zone');
      }
    }
  };

  const handleToggleActive = async (zone) => {
    try {
      await apiService.updateZoneLivraison(commercantId, zone.id, {
        actif: !zone.actif
      });
      await fetchZones();
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      alert('Erreur lors du changement de statut de la zone');
    }
  };

  const filteredZones = zones.filter(zone => {
    const matchesSearch = zone.nom?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Calculer le centre de toutes les zones pour la carte récapitulative
  const getMapCenter = () => {
    const allPolygons = zones
      .filter(z => z.polygon)
      .map(z => normalizePolygonFromApi(z.polygon))
      .flat();
    
    if (allPolygons.length === 0) {
      // Si pas de zones, utiliser l'adresse du commerçant
      if (commercantLocation) {
        return [commercantLocation.lat, commercantLocation.lng];
      }
      return [48.8566, 2.3522]; // Paris par défaut
    }

    const sum = allPolygons.reduce(
      (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
      { lat: 0, lng: 0 }
    );
    return [sum.lat / allPolygons.length, sum.lng / allPolygons.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Zones de livraison ({filteredZones.length})</h3>
          <p className="text-sm text-gray-500">Gérez les zones de livraison et leurs tarifs</p>
        </div>
        <button
          onClick={handleAddZone}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Ajouter une zone</span>
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une zone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Afficher les zones inactives</span>
        </label>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Carte récapitulative */}
      {zones.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Vue d'ensemble des zones</h4>
          <div 
            data-overview-map 
            style={{ height: '300px', width: '100%' }} 
            className="border border-gray-300 rounded-md overflow-hidden"
          >
            <MapContainer
              key="zones-overview-map" // Key stable pour éviter les remounts inutiles
              center={getMapCenter()}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {zones
                .filter(z => z.polygon && z.actif)
                .map((zone, index) => {
                  const polygon = toLeafletFormat(normalizePolygonFromApi(zone.polygon));
                  if (polygon.length < 2) return null;
                  
                  return (
                    <Polygon
                      key={zone.id}
                      positions={polygon}
                      pathOptions={{
                        color: zone.actif ? '#10b981' : '#ef4444',
                        fillColor: zone.actif ? '#10b981' : '#ef4444',
                        fillOpacity: 0.2,
                        weight: 2
                      }}
                    />
                  );
                })}
            </MapContainer>
          </div>
        </div>
      )}

      {/* Liste des zones */}
      {filteredZones.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune zone de livraison</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm 
              ? 'Aucune zone ne correspond à votre recherche.'
              : 'Commencez par ajouter votre première zone de livraison.'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={handleAddZone}
              className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une zone</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredZones.map((zone) => {
            const polygon = normalizePolygonFromApi(zone.polygon);
            const pointCount = polygon.length;
            
            return (
              <div key={zone.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* En-tête de la zone */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium text-gray-900 text-lg">{zone.nom}</h4>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      zone.actif 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {zone.actif ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Informations de la zone */}
                  <div className="space-y-3 mb-4">
                    {pointCount > 0 && (
                      <div className="flex items-center space-x-2">
                        <Navigation className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {pointCount} point{pointCount !== 1 ? 's' : ''} du polygone
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Euro className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="text-xs text-gray-500">Frais</p>
                          <p className="text-sm font-medium text-gray-900">
                            {zone.fraisCents ? (zone.fraisCents / 100).toFixed(2) : '0.00'}€
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="text-xs text-gray-500">Délai</p>
                          <p className="text-sm font-medium text-gray-900">
                            {zone.delaiMin || 30}-{zone.delaiMax || 60}min
                          </p>
                        </div>
                      </div>
                    </div>

                    {zone.montantMinimumCents > 0 && (
                      <div className="text-sm text-gray-600">
                        <strong>Minimum:</strong> {(zone.montantMinimumCents / 100).toFixed(2)}€
                      </div>
                    )}

                    {zone.livraisonGratuiteCents && (
                      <div className="text-sm text-green-600">
                        <strong>Gratuit à partir de:</strong> {(zone.livraisonGratuiteCents / 100).toFixed(2)}€
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditZone(zone)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                      >
                        <Edit className="h-3 w-3" />
                        <span>Modifier</span>
                      </button>
                      <button
                        onClick={() => handleToggleActive(zone)}
                        className={`flex items-center space-x-1 px-3 py-1.5 text-sm font-medium rounded-md ${
                          zone.actif
                            ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                            : 'text-green-600 bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        {zone.actif ? (
                          <>
                            <ToggleLeft className="h-3 w-3" />
                            <span>Désactiver</span>
                          </>
                        ) : (
                          <>
                            <ToggleRight className="h-3 w-3" />
                            <span>Activer</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteZone(zone.id)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                      >
                        <Trash2 className="h-3 w-3" />
                        <span>Supprimer</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal d'ajout */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          // Reset complet à la fermeture pour éviter les résidus
          setShowAddModal(false);
          setSelectedZone(null);
        }}
        title="Ajouter une zone de livraison"
        maxWidth="max-w-4xl"
      >
        {/* Key unique qui change à chaque ouverture pour forcer le remount complet */}
        <ZoneLivraisonEditor
          key={`add-zone-${addModalKey}`}
          commercantId={commercantId}
          defaultCenter={commercantLocation ? [commercantLocation.lat, commercantLocation.lng] : null}
          existingZones={zones.filter(z => z.actif && z.polygon)} // Passer les zones actives existantes pour référence
          onSave={handleSaveZone}
          onCancel={() => {
            setShowAddModal(false);
            setSelectedZone(null);
          }}
        />
      </Modal>

      {/* Modal d'édition */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          // Reset complet à la fermeture pour éviter les résidus
          setShowEditModal(false);
          setSelectedZone(null);
        }}
        title="Modifier la zone de livraison"
        maxWidth="max-w-4xl"
      >
        {/* Key unique basée sur selectedZone.id pour forcer le remount seulement quand la zone change */}
        {selectedZone && (
          <ZoneLivraisonEditor
            key={`edit-zone-${selectedZone.id}`}
            zone={selectedZone}
            commercantId={commercantId}
            onSave={handleSaveZone}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedZone(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default ZonesLivraisonManager;
